import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileSignature, RotateCcw, Loader2, Printer, ShieldCheck, AlertCircle, Building2, Landmark, History } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { dateLocale } from '@/lib/locale';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InvoiceBadge } from './InvoiceBadge';
import { eimzoInit, eimzoListCertificates, eimzoSign, type EimzoCert } from '@/lib/eimzo';
import { apiErrorMessage, type Invoice, type InvoiceEvent, type InvoiceParty, type InvoiceSignature } from '@/lib/invoices';
import { useAdminInvoice, useCaseInvoice } from '@/hooks/use-invoices';

function Row({ label, value, mono }: { label: string; value: unknown; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-foreground text-right ${mono ? 'font-mono text-xs' : ''}`}>{value === null || value === undefined || value === '' ? '—' : String(value)}</span>
    </div>
  );
}

function PartyTab({ p }: { p: InvoiceParty | null }) {
  const { t } = useTranslation();
  if (!p) return <p className="text-sm text-muted-foreground py-4">{t('invoice.no_data')}</p>;
  return (
    <div className="divide-y divide-border">
      <Row label={t('invoice.party.name')} value={p.name} />
      <Row label={t('invoice.party.tin')} value={p.tin} mono />
      <Row label={t('invoice.party.address')} value={p.address} />
      <Row label={t('invoice.party.oked')} value={p.oked} mono />
      <Row label={t('invoice.party.vat_reg_code')} value={p.vatRegCode} mono />
      <Row label={t('invoice.party.bank_id')} value={p.bankId} mono />
      <Row label={t('invoice.party.account')} value={p.account} mono />
      <Row label={t('invoice.party.header')} value={p.header} />
      <Row label={t('invoice.party.accountant')} value={p.accountant} />
      {p.branchName && <Row label={t('invoice.party.branch')} value={`${p.branchName}${p.branchCode ? ` (${p.branchCode})` : ''}`} />}
    </div>
  );
}

function SignatureBlock({ title, sig, pending }: { title: string; sig: InvoiceSignature | null; pending: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2 pb-3 mb-1 border-b border-border">
        <ShieldCheck size={16} className={sig ? 'text-success' : 'text-muted-foreground'} />
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {sig?.mock && <span className="text-[10px] rounded bg-muted px-1.5 py-0.5 text-muted-foreground">{t('invoice.signature.mock')}</span>}
      </div>
      {sig ? (
        <div className="pt-2">
          <Row label={t('invoice.signature.signer')} value={sig.signer_name} />
          <Row label={t('invoice.signature.serial')} value={sig.certificate_serial} mono />
          {sig.valid_to && <Row label={t('invoice.signature.valid_to')} value={sig.valid_to} />}
          <Row label={t('invoice.signature.signed_at')} value={sig.signed_at ? new Date(sig.signed_at).toLocaleString(dateLocale()) : null} />
          <Row label={t('invoice.signature.state')} value={t('invoice.signature.signed')} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-6">{pending}</p>
      )}
    </div>
  );
}

/** E-IMZO key picker + PKCS#7 signing of `invoice.signing_data`; `buyer` signs as the Hisob MMT director. */
export function SignDialog({ invoice, open, onOpenChange, onDone, buyer }: { invoice: Invoice; open: boolean; onOpenChange: (v: boolean) => void; onDone: () => void; buyer?: boolean }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [certs, setCerts] = useState<EimzoCert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      await eimzoInit();
      setCerts(await eimzoListCertificates());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'eimzo.errors.generic');
    } finally { setLoading(false); }
  };

  const submit = async (signature: Record<string, unknown>) => {
    const { error } = buyer
      ? await apiClient.POST('/admin/invoices/{invoice}/buyer-sign', { params: { path: { invoice: invoice.id } }, body: { signature } as never })
      : await apiClient.POST('/invoices/{invoice}/sign', { params: { path: { invoice: invoice.id } }, body: { signature } as never });
    if (error) throw error;
  };

  const run = async (key: string, signature: () => Promise<Record<string, unknown>>) => {
    setSigning(key);
    try {
      await submit(await signature());
      toast.success(t(buyer ? 'invoice.toast.buyer_signed' : 'invoice.toast.signed'));
      onDone(); onOpenChange(false);
    } catch (e) {
      toast.error(t(apiErrorMessage(e, 'invoice.toast.sign_failed')));
    } finally { setSigning(null); }
  };

  const signWith = (c: EimzoCert) => run(c.serial || c.name, async () => {
    const pkcs7 = await eimzoSign(c, invoice.signing_data ?? '');
    return { pkcs7, serial: c.serial, cn: c.cn, tin: c.tin, pinfl: c.pinfl, valid_to: c.validTo };
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (v && !certs && !loading) load(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('invoice.sign_dialog.title')}</DialogTitle>
          <DialogDescription>{t(buyer ? 'invoice.sign_dialog.description_buyer' : 'invoice.sign_dialog.description')}</DialogDescription>
        </DialogHeader>
        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 size={16} className="animate-spin" /> {t('invoice.sign_dialog.connecting')}</div>}
        {error && (
          <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
            <p className="text-foreground">{t(error)}</p>
            <a href="https://e-imzo.soliq.uz" target="_blank" rel="noreferrer" className="text-primary underline text-xs">{t('invoice.sign_dialog.download')}</a>
            <Button variant="outline" size="sm" className="mt-2 ml-2" onClick={load}>{t('invoice.sign_dialog.reconnect')}</Button>
          </div>
        )}
        {certs && certs.length === 0 && <p className="text-sm text-muted-foreground">{t('invoice.sign_dialog.no_keys')}</p>}
        {certs && certs.length > 0 && (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {certs.map(c => (
              <button key={c.serial + c.name} disabled={c.expired || !!signing} onClick={() => signWith(c)}
                className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted/50 disabled:opacity-50">
                <p className="text-sm font-medium text-foreground">{c.cn}</p>
                <p className="text-xs text-muted-foreground">{c.org || t('invoice.sign_dialog.individual')} · INN {c.tin || '—'} · PINFL {c.pinfl || '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {t('invoice.signature.serial')}: {c.serial} · {c.expired ? t('invoice.sign_dialog.expired') : t('invoice.sign_dialog.valid_to', { date: c.validTo })}
                </p>
                {signing === (c.serial || c.name) && <Loader2 size={14} className="animate-spin mt-1" />}
              </button>
            ))}
          </div>
        )}
        <DialogFooter className="sm:justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => run('mock', async () => ({ mock: true }))} disabled={!!signing}>
            {signing === 'mock' && <Loader2 size={14} className="mr-1 animate-spin" />} {t('invoice.sign_dialog.mock')}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Merchant view of a murabaha case's invoice: header, sign/retry actions, the document itself. */
export function InvoiceCard({ caseId, canSign, canRetry }: { caseId: number; canSign?: boolean; canRetry?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [signOpen, setSignOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const { data: invoice, isLoading } = useCaseInvoice(caseId);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['case-invoice', caseId] });
    qc.invalidateQueries({ queryKey: ['/murabaha/cases'] });
  };

  const retry = async () => {
    if (!invoice) return;
    setRetrying(true);
    try {
      const { error } = await apiClient.POST('/invoices/{invoice}/retry', { params: { path: { invoice: invoice.id } } });
      if (error) throw error;
      toast.success(t('invoice.toast.retried'));
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e, t('common.error'))); }
    finally { setRetrying(false); }
  };

  if (isLoading) return null;

  if (!invoice) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2"><FileSignature size={16} className="text-primary" /><h2 className="text-sm font-semibold">{t('invoice.title')}</h2></div>
        <p className="text-sm text-muted-foreground mt-2">{t('invoice.none')}</p>
      </div>
    );
  }

  const canResend = invoice.status === 'signed' && !!invoice.last_error;

  return (
    <div className="space-y-4">
      <InvoiceHeader invoice={invoice}>
        {canRetry && (invoice.status === 'error' || canResend) && (
          <Button size="sm" variant="outline" onClick={retry} disabled={retrying}>
            {retrying ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RotateCcw size={14} className="mr-1.5" />} {t(canResend ? 'invoice.actions.resend' : 'invoice.actions.retry')}
          </Button>
        )}
        {canSign && invoice.status === 'created' && (
          <Button size="sm" className="md:ml-auto" onClick={() => setSignOpen(true)}><FileSignature size={14} className="mr-1.5" /> {t('invoice.actions.sign')}</Button>
        )}
      </InvoiceHeader>

      <InvoiceDocument invoice={invoice} />

      {canSign && <SignDialog invoice={invoice} open={signOpen} onOpenChange={setSignOpen} onDone={refresh} />}
    </div>
  );
}

export function InvoiceHeader({ invoice, director, children }: { invoice: Invoice; director?: boolean; children?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <FileSignature size={19} className="text-primary mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">{t('invoice.title')} {invoice.doc_num}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(invoice.doc_date).toLocaleDateString(dateLocale())}
              {invoice.merchant && <> · {invoice.merchant.name}</>}
              {invoice.external_id && <> · Hujjat.uz ID: <span className="font-mono">{invoice.external_id}</span></>}
            </p>
          </div>
        </div>
        <InvoiceBadge status={invoice.status} director={director} />
      </div>

      <div className="border-t border-border bg-muted/30 px-5 py-3 flex flex-wrap items-center gap-2">
        {invoice.external_id && (
          <Button size="sm" variant="outline" onClick={() => window.print()}><Printer size={14} className="mr-1.5" /> {t('invoice.actions.print')}</Button>
        )}
        {children}
      </div>

      {invoice.missing_fields.length > 0 && (
        <div className="mx-5 mb-3 flex items-start gap-2 rounded-md bg-warning/5 border border-warning/30 p-3 text-xs text-foreground">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
          <span>{t('invoice.missing_title')}: {invoice.missing_fields.map(f => t(`invoice.missing.${f}`, { defaultValue: f })).join(', ')}</span>
        </div>
      )}
      {invoice.last_error && invoice.missing_fields.length === 0 && (
        <div className="m-5 mt-0 flex items-start gap-2 rounded-md bg-destructive/5 border border-destructive/30 p-3 text-xs text-destructive">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {invoice.last_error}
        </div>
      )}
    </section>
  );
}

function eventLabel(e: InvoiceEvent, t: (k: string, o?: Record<string, unknown>) => string): string {
  return t(`invoice.events.${e.event_type}`, { defaultValue: e.to_status ? t(`invoice.status.${e.to_status}`, { defaultValue: e.to_status }) : e.event_type });
}

export function InvoiceDocument({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  const products = invoice.products ?? [];
  const events = invoice.events ?? [];
  const awaitingBuyer = ['sent', 'in_review'].includes(invoice.status);
  return (
    <>
      <article className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-8">
        <header className="text-center border-b border-border pb-6">
          <p className="text-xs uppercase text-muted-foreground">{t('invoice.document.kind')}</p>
          <h3 className="text-xl font-semibold text-foreground mt-2">{t('invoice.title')}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {invoice.contract_num
              ? t('invoice.document.to_contract', { date: invoice.contract_date ?? invoice.doc_date, num: invoice.contract_num })
              : t('invoice.document.to_general_contract', { date: invoice.contract_date ?? invoice.doc_date })}
          </p>
          <p className="text-sm font-medium text-foreground mt-1">№ {invoice.doc_num} · {invoice.doc_date}</p>
        </header>

        <section className="grid gap-8 md:grid-cols-2">
          <div>
            <div className="flex items-center gap-2 mb-3"><Building2 size={16} className="text-primary" /><h4 className="text-xs font-semibold uppercase text-foreground">{t('invoice.document.seller')}</h4></div>
            <PartyTab p={invoice.seller} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3"><Landmark size={16} className="text-primary" /><h4 className="text-xs font-semibold uppercase text-foreground">{t('invoice.document.buyer')}</h4></div>
            <PartyTab p={invoice.buyer} />
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase text-foreground mb-3">{t('invoice.document.products')}</h4>
          {products.length === 0 ? <p className="text-sm text-muted-foreground py-4">{t('invoice.document.no_products')}</p> : (
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="w-full min-w-[860px] text-xs [&_th]:px-2 [&_th]:py-2.5 [&_td]:px-2 [&_td]:py-2.5 [&_th]:whitespace-nowrap [&_th]:font-medium">
                <thead className="bg-muted/50"><tr className="border-b border-border text-muted-foreground">
                  <th className="text-left">№</th>
                  <th className="text-left">{t('invoice.columns.name')}</th>
                  <th className="text-left">{t('invoice.columns.catalog_name')}</th>
                  <th className="text-left">{t('invoice.columns.catalog_code')}</th>
                  <th className="text-left">{t('invoice.columns.barcode')}</th>
                  <th className="text-left">{t('invoice.columns.unit')}</th>
                  <th className="text-right">{t('invoice.columns.amount')}</th>
                  <th className="text-right">{t('invoice.columns.price')}</th>
                  <th className="text-right">{t('invoice.columns.vat_rate')}</th>
                  <th className="text-right">{t('invoice.columns.vat_sum')}</th>
                  <th className="text-right">{t('invoice.columns.final_sum')}</th>
                </tr></thead>
                <tbody>
                  {products.map(x => (
                    <tr key={x.num} className="border-b border-border last:border-0 align-top">
                      <td>{x.num}</td><td className="font-medium text-foreground min-w-[120px]">{x.name}</td>
                      <td className="text-muted-foreground min-w-[120px]">{x.catalogName || '—'}</td>
                      <td className="font-mono text-[11px] text-muted-foreground">{x.catalogCode || '—'}</td>
                      <td className="font-mono text-[11px] text-muted-foreground">{x.barcode || '—'}</td>
                      <td>{x.packageName}</td><td className="text-right">{x.amount}</td>
                      <td className="text-right whitespace-nowrap">{formatUZS(Number(x.price))}</td>
                      <td className="text-right">{x.vatRate}%</td>
                      <td className="text-right whitespace-nowrap">{formatUZS(Number(x.vatSum))}</td>
                      <td className="text-right font-semibold text-foreground whitespace-nowrap">{formatUZS(Number(x.finalSum))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 ml-auto w-full max-w-sm divide-y divide-border border-y border-border">
            <Row label={t('invoice.totals.without_vat')} value={formatUZS(Number(invoice.total_sum))} />
            <Row label={t('invoice.totals.vat')} value={formatUZS(Number(invoice.vat_sum))} />
            <div className="flex justify-between gap-4 py-3 text-sm font-semibold"><span>{t('invoice.totals.total')}</span><span>{formatUZS(Number(invoice.final_sum))}</span></div>
          </div>
        </section>

        {invoice.reject_reason && (
          <div className="flex items-start gap-2 rounded-md bg-destructive/5 border border-destructive/30 p-3 text-sm text-destructive">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span><b>{t('invoice.rejected_by_hisob')}:</b> {invoice.reject_reason}</span>
          </div>
        )}
        <section className="grid gap-8 md:grid-cols-2">
          <SignatureBlock title={t('invoice.signature.seller_title')} sig={invoice.seller_signature} pending={t('invoice.signature.not_signed')} />
          <SignatureBlock title={t('invoice.signature.buyer_title')} sig={invoice.buyer_signature} pending={t(awaitingBuyer ? 'invoice.signature.awaiting' : 'invoice.signature.after_seller')} />
        </section>
      </article>

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4"><History size={16} className="text-primary" /><h3 className="text-sm font-semibold text-foreground">{t('invoice.history')}</h3></div>
        {events.length === 0 ? <p className="text-sm text-muted-foreground">{t('invoice.no_history')}</p> : (
          <ol className="space-y-3">
            {events.map(e => {
              const details = (Array.isArray(e.details) ? {} : e.details) as Record<string, unknown>;
              return (
                <li key={e.id} className="grid gap-1 text-xs sm:grid-cols-[150px_1fr]">
                  <span className="text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString(dateLocale())}</span>
                  <span className="text-foreground">
                    {eventLabel(e, t)}{e.actor_name && <span className="text-muted-foreground"> · {e.actor_name}</span>}
                    {typeof details.reason === 'string' && <span className="block text-muted-foreground mt-0.5">{t('invoice.reason')}: {details.reason}</span>}
                    {typeof details.error === 'string' && <span className="block text-destructive mt-0.5">{details.error}</span>}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </>
  );
}

/** Read-only platform view of an invoice (admin contract detail); a stub when the case has none, as Lovable. */
export function AdminInvoicePanel({ invoiceId }: { invoiceId: number | null }) {
  const { t } = useTranslation();
  const { data: invoice, isLoading } = useAdminInvoice(invoiceId);
  if (invoiceId === null) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2"><FileSignature size={16} className="text-primary" /><h2 className="text-sm font-semibold">{t('invoice.title')}</h2></div>
        <p className="text-sm text-muted-foreground mt-2">{t('invoice.none')}</p>
      </div>
    );
  }
  if (isLoading || !invoice) return null;
  return (
    <div className="space-y-4">
      <InvoiceHeader invoice={invoice} director />
      <InvoiceDocument invoice={invoice} />
    </div>
  );
}
